/**
 * The request body for `POST /home_run_harvest {action:"release"}` — the CIO's explicit,
 * RECORDED early release of capital sitting under a home-run redeployment lockout.
 *
 * The rule stays; this is the bypass, and the reasoning capture is the whole point. Without
 * it he can already ignore the number on screen and buy anyway (the lockout is advisory and
 * blocks nothing). This makes the override deliberate and leaves a track record.
 *
 * =========================================================================================
 * THE SERVER CONTRACT, TRANSCRIBED FROM THE HANDLER — NOT FROM MEMORY.
 *
 * HelionBackend/home_run_harvest/__init__.py
 *   :52   body   = _read_json(req)                       # non-dict body -> {} -> 400 later
 *   :53   action = str(body.get("action") or "record").strip().lower()
 *   :59   if action in {"release", "cancel", "unlock"}:   # THE ONLY three that release
 *   :60       lock_id = body.get("lockId") or body.get("lock_id")
 *   :61-62    if lock_id in (None, ""): -> 400 "lockId is required for release/cancel actions."
 *   :63-67    release_home_run_lock(
 *                 int(lock_id),
 *                 reason      = str(body.get("reason")     or "manual CIO release").strip(),
 *                 recorded_by = str(body.get("recordedBy") or body.get("recorded_by")
 *                                   or "CIO").strip() or "CIO",
 *             )
 *   :68       return _response(result, 200)
 *   :71-72  except ValueError -> 400 {"error": str(exc)}
 *   :73-78  except pyodbc.Error / Exception -> 500 {"error": ..., "details": ...}
 *
 * So the ENTIRE accepted vocabulary of this call is four keys: `action`, `lockId`,
 * `reason`, `recordedBy`. There is no amount field, no category field, no tenor field and
 * no intended-use field — sending one is silently ignored, and NAMING one differently is
 * how the previous attempt shipped four dropdown values that were guaranteed 400s. The
 * body this module builds contains those four keys and nothing else.
 *
 * HelionBackend/strategy/home_run_redeployment.py release_home_run_lock()
 *   RESTATED 2026-09-04 (P2-REASONLENGTH). The Notes append used to happen inside the
 *   UPDATE, as `Notes = COALESCE(Notes + CHAR(10), '') + ?` with no truncation — which is
 *   what made an over-long reason a 500 and, via the client-side budget that guarded
 *   against it, made the release BUTTON disappear on a lock whose notes were already long.
 *   The composition now happens in Python and is clipped there:
 *
 *             SELECT Notes FROM dbo.HomeRunHarvestLocks
 *              WHERE LockId = ? AND Status = 'ACTIVE';        -- no row -> ValueError -> 400
 *             new_notes, clip = _fit_release_note(existing, release_note_line(who, reason))
 *             UPDATE dbo.HomeRunHarvestLocks
 *                SET Status = 'RELEASED', Notes = ?, UpdatedAtUTC = SYSUTCDATETIME()
 *              WHERE LockId = ? AND Status = 'ACTIVE';
 *             if cursor.rowcount <= 0: raise ValueError(...)  -> HTTP 400, nothing released
 *             insert_trade_audit(... HOME_RUN_LOCK_RELEASED, reason=reason, entry_user=...)
 *             return {"status": "success", "lockId": int(lock_id), "released": True,
 *                     "notesClipped": bool, "notesClipping": {...}}
 *
 *   `_fit_release_note` gives the RELEASE LINE priority: the older note is clipped to make
 *   room, never the reason, and the response reports exactly what was dropped. So a
 *   release can no longer fail — or be withheld — because a note was long.
 *
 * FULL RELEASE ONLY. The UPDATE is all-or-nothing: there is no partial column and no
 * remaining-amount arithmetic. A partial release needs a migration, which v1 excludes.
 *
 * =========================================================================================
 * WHY THE REASON IS LENGTH-BUDGETED HERE.
 *
 * `dbo.HomeRunHarvestLocks.Notes` is nvarchar(1000) (ensure_home_run_schema in
 * home_run_redeployment.py; confirmed in HelionBackend/schema/schema_snapshot.json), and
 * the release APPENDS to it. A release must never fail — or be withheld — because the CIO
 * typed too much, so the reason is budgeted before it is sent.
 *
 * WHAT THE BUDGET IS MEASURED AGAINST CHANGED, 2026-09-04 (P2-REASONLENGTH). It used to be
 * "whatever is left after the note already on this lock", which is the arithmetic the old
 * in-SQL concatenation forced. That budget reaches ZERO: a lock recorded from the Trade
 * Desk carries the ticket's own "why" as its note, and past roughly 919 characters the
 * modal disabled its own Release button and told the CIO to ask the desk — the control was
 * withdrawn on the path most harvests take, because he had explained himself thoroughly.
 * The budget is now the release LINE's own capacity (`releaseReasonAllowance`), which never
 * depends on the old note; the server clips the older note to make room and reports it, and
 * `describeReleaseClipping` warns before the click. `releaseReasonMaxChars` survives as the
 * "fits with nothing clipped" figure, which is advisory rather than a limit.
 *
 * The TradeAudit row is NOT the binding constraint: insert_trade_audit goes through
 * insert_row -> _coerce_value_for_column (trading_desk_db.py:140-144), which slices text to
 * the column width, so it truncates instead of raising.
 *
 * =========================================================================================
 * WHY THE CATEGORY IS A PREFIX AND NOT A FIELD.
 *
 * There is no category column and v1 adds no schema. The category is therefore composed
 * INTO the reason string with a machine-parseable prefix — `[SUPERIOR_OPPORTUNITY] ...` —
 * so a later migration can lift it into a real column without losing what v1 collected.
 * The server does no validation of `reason` at all, so this vocabulary is ours alone and
 * cannot disagree with anything the handler enforces. It is deliberately a CLOSED set: a
 * free-text-only record cannot be counted, and counting is what makes the brake work.
 */

/* --------------------------------------------------------------------------------------
 * The four keys the handler reads. Literals, not derived — see the header.
 * ----------------------------------------------------------------------------------- */

/** home_run_harvest/__init__.py:59. "cancel" and "unlock" hit the same branch; "release"
 *  is the one that says what it does. Lower case: the handler lowercases anyway, but a
 *  body that already matches needs no coercion to be correct. */
export const HARVEST_RELEASE_ACTION = "release";

/** dbo.HomeRunHarvestLocks.Notes — nvarchar(1000). */
export const LOCK_NOTES_MAX_CHARS = 1000;

/** home_run_redeployment.py:675 — f"Released by {recorded_by}: {reason}". */
export const RELEASE_NOTE_PREFIX = "Released by ";
export const RELEASE_NOTE_JOINER = ": ";

/** Enough to be a reason, not so much that it is a chore. Ours, not the server's. */
export const MIN_RELEASE_REASONING_CHARS = 12;

export type ReleaseReasonCategory =
  | "SUPERIOR_OPPORTUNITY"
  | "REGIME_CHANGE"
  | "SIZING_OR_REBALANCE"
  | "LIQUIDITY_NEED"
  | "RECORDING_ERROR"
  | "OTHER";

/** The closed set offered in the UI. Codes are what get written into the reason string and
 *  what a later migration will parse, so they are stable identifiers — change a label
 *  freely, never a code. */
export const RELEASE_REASON_CATEGORIES: ReadonlyArray<{
  code: ReleaseReasonCategory;
  label: string;
  /** Shown under the picker so the choice means the same thing each time. */
  help: string;
}> = [
  {
    code: "SUPERIOR_OPPORTUNITY",
    label: "Better use for the capital",
    help: "A specific position you want this money in now, named in the reasoning below.",
  },
  {
    code: "REGIME_CHANGE",
    label: "Regime change",
    help: "The volatility or rate picture has moved enough that waiting out the clock is the wrong call.",
  },
  {
    code: "SIZING_OR_REBALANCE",
    label: "Sizing / rebalance requirement",
    help: "The book needs this capital to hit an allocation the engine or policy is asking for.",
  },
  {
    code: "LIQUIDITY_NEED",
    label: "Liquidity need",
    help: "Redemptions, expenses or another obligation needs the cash back in the deployable pool.",
  },
  {
    code: "RECORDING_ERROR",
    label: "Lock recorded in error",
    help: "The harvest or its quantity was mis-recorded, so this lockout should not exist.",
  },
  {
    code: "OTHER",
    label: "Other",
    help: "Anything else. Say what it is — an uncategorised release is the hardest one to review later.",
  },
];

const CATEGORY_CODES: ReadonlySet<string> = new Set(RELEASE_REASON_CATEGORIES.map((c) => c.code));

export function isReleaseReasonCategory(v: unknown): v is ReleaseReasonCategory {
  return typeof v === "string" && CATEGORY_CODES.has(v);
}

/* --------------------------------------------------------------------------------------
 * Reason composition and its length budget.
 * ----------------------------------------------------------------------------------- */

/** The literal tag written into the reason. Machine-parseable, deliberately boring. */
export function categoryTag(category: ReleaseReasonCategory): string {
  return `[${category}] `;
}

/**
 * How many characters of `reason` the lock row can still take.
 *
 * The server writes `COALESCE(Notes + CHAR(10), '') + "Released by {who}: {reason}"` back
 * into an nvarchar(1000) column. COALESCE collapses to '' only when Notes IS NULL — an
 * existing note therefore also costs its own length plus the one CHAR(10).
 */
export function releaseReasonMaxChars(input: {
  /** The lock's current Notes, from activeLocks[].notes. Unknown -> pass null. */
  existingNotes?: string | null;
  /** Exactly what will be sent as recordedBy, AFTER its own defaulting. */
  recordedBy: string;
}): number {
  const notes = typeof input.existingNotes === "string" ? input.existingNotes : "";
  const existingCost = notes.length > 0 ? notes.length + 1 : 0; // + CHAR(10)
  const prefixCost = RELEASE_NOTE_PREFIX.length + input.recordedBy.length + RELEASE_NOTE_JOINER.length;
  return Math.max(0, LOCK_NOTES_MAX_CHARS - existingCost - prefixCost);
}

/* --------------------------------------------------------------------------------------
 * P2-REASONLENGTH, 2026-09-04. THE BUDGET USED TO STRAND THE BUTTON.
 *
 * `releaseReasonMaxChars` above answers "how much reason fits ALONGSIDE the note already on
 * this lock". That was the only budget the control had, and it goes to zero. A lock recorded
 * from the Trade Desk carries a note composed as
 *     "Home-run trim recorded from the Trade Desk sell lane (ticket #N). {the ticket's why}"
 * clipped to 1000 characters — so at roughly 919 characters of prior note the budget fell
 * below the 12-character reasoning minimum, the modal disabled its own button and told the
 * CIO to "ask the desk to handle this one directly". The Trade Desk is the path MOST
 * harvests take. The control he asked for was being withdrawn because he had written a
 * thorough explanation on the ticket.
 *
 * The server now composes the new Notes value in Python (`_fit_release_note`,
 * home_run_redeployment.py) instead of concatenating inside the UPDATE, and gives the
 * RELEASE LINE priority: the older note is clipped to make room, never the other way round,
 * and the response reports what was clipped. So the client's budget is no longer "what is
 * left over" but "what the release line itself can hold" — which is never zero.
 *
 * The reasoning is not lost when the note is clipped: insert_trade_audit writes the full
 * reason to dbo.TradeAudit under HOME_RUN_LOCK_RELEASED, truncating per column rather than
 * raising.
 * ----------------------------------------------------------------------------------- */

/** What the release line alone can hold once "Released by {who}: " is paid for. Always > 0
 *  for any realistic user string, and independent of what is already on the lock. */
export function releaseLineReasonCapacity(recordedBy: string): number {
  const prefixCost = RELEASE_NOTE_PREFIX.length + recordedBy.length + RELEASE_NOTE_JOINER.length;
  return Math.max(0, LOCK_NOTES_MAX_CHARS - prefixCost);
}

export type ReleaseReasonAllowance = {
  /** What the box should allow. Never depends on the lock's existing notes. */
  maxChars: number;
  /** How much would fit with the existing note left intact. May be 0; that is fine now. */
  fitsAlongsideExistingNotes: number;
  /** Length of the note already on the lock, 0 when there is none or it is unknown. */
  existingNotesLength: number;
};

export function releaseReasonAllowance(input: {
  existingNotes?: string | null;
  recordedBy: string;
}): ReleaseReasonAllowance {
  const notes = typeof input.existingNotes === "string" ? input.existingNotes : "";
  return {
    maxChars: releaseLineReasonCapacity(input.recordedBy),
    fitsAlongsideExistingNotes: releaseReasonMaxChars(input),
    existingNotesLength: notes.length,
  };
}

/**
 * What the server will have to trim off this lock's EXISTING notes to record this release,
 * given the reason as it stands. Shown to the CIO before he commits so a clip is something
 * he was told about rather than something he discovers.
 *
 * Mirrors `_fit_release_note`: existing + CHAR(10) + line, with the line winning.
 */
export function describeReleaseClipping(input: {
  existingNotes?: string | null;
  recordedBy: string;
  /** The composed reason (tag + text) as it will be sent. */
  reason: string;
}): { willClip: boolean; droppedFromExistingNotes: number; note: string | null } {
  const notes = typeof input.existingNotes === "string" ? input.existingNotes : "";
  if (notes.length === 0) return { willClip: false, droppedFromExistingNotes: 0, note: null };
  const lineLength =
    RELEASE_NOTE_PREFIX.length + input.recordedBy.length + RELEASE_NOTE_JOINER.length + input.reason.length;
  const overflow = notes.length + 1 + lineLength - LOCK_NOTES_MAX_CHARS;
  if (overflow <= 0) return { willClip: false, droppedFromExistingNotes: 0, note: null };
  const dropped = Math.min(notes.length, overflow);
  return {
    willClip: true,
    droppedFromExistingNotes: dropped,
    note:
      `Recording this will trim ${dropped} character${dropped === 1 ? "" : "s"} off the end of this ` +
      `lock's earlier notes to make room — the release record takes priority. Nothing is lost: the ` +
      `harvest note and your full reasoning both stay on the audit trail.`,
  };
}

/**
 * `[CATEGORY] free text`, clipped so the whole thing fits `maxChars`.
 *
 * Clipping takes it out of the FREE TEXT, never the tag: a truncated tag is unparseable and
 * the category is the part a later migration needs. If even the tag will not fit (a lock
 * whose notes are already at the column limit) the composed string is clipped whole — an
 * ugly record beats a 500 that rolls the release back.
 */
export function composeReleaseReason(
  category: ReleaseReasonCategory,
  reasoning: string,
  maxChars: number
): string {
  const tag = categoryTag(category);
  const text = String(reasoning ?? "").trim().replace(/\s+/g, " ");
  const full = `${tag}${text}`;
  if (maxChars <= 0) return "";
  if (full.length <= maxChars) return full;
  if (maxChars <= tag.length) return full.slice(0, maxChars);
  return `${tag}${text.slice(0, maxChars - tag.length)}`.trimEnd();
}

/** Reads a composed reason back. Used for the history list; also what a migration would do. */
export function parseComposedReason(reason: string | null | undefined): {
  category: ReleaseReasonCategory | null;
  reasoning: string;
} {
  const raw = typeof reason === "string" ? reason.trim() : "";
  const m = /^\[([A-Z_]+)\]\s*([\s\S]*)$/.exec(raw);
  if (m && isReleaseReasonCategory(m[1])) {
    return { category: m[1], reasoning: m[2].trim() };
  }
  return { category: null, reasoning: raw };
}

/* --------------------------------------------------------------------------------------
 * The body.
 * ----------------------------------------------------------------------------------- */

export type HarvestReleaseBody = {
  action: string;
  lockId: number;
  reason: string;
  recordedBy: string;
};

export type HarvestReleaseInput = {
  lockId: number | string | null | undefined;
  category: ReleaseReasonCategory;
  reasoning: string;
  /** Signed-in email. Falsy -> "CIO", matching the handler's own default. */
  recordedBy?: string | null;
  /** activeLocks[].notes for THIS lock, so the reason can be budgeted. */
  existingNotes?: string | null;
};

/**
 * Throws rather than sending something the handler will reject or the UPDATE will refuse.
 * Everything it validates is a rule the SERVER imposes:
 *   - lockId must survive `int(lock_id)`               (__init__.py:63)
 *   - lockId may not be None or ""                     (__init__.py:61-62)
 *   - reason must be non-empty after `.strip()`, or the handler silently substitutes
 *     "manual CIO release" and the whole recorded-reasoning premise is void (__init__.py:65)
 *   - the composed reason must fit the Notes column     (home_run_redeployment.py:672)
 * plus one rule of our own: a category and real reasoning, because a release with neither
 * is indistinguishable from ignoring the lockout.
 */
export function buildHarvestReleaseBody(input: HarvestReleaseInput): HarvestReleaseBody {
  const rawId = input.lockId;
  const lockId = typeof rawId === "string" ? Number(rawId.trim()) : rawId;
  if (typeof lockId !== "number" || !Number.isFinite(lockId) || !Number.isInteger(lockId) || lockId <= 0) {
    throw new Error("lockId must be a positive whole number identifying the lock to release.");
  }

  if (!isReleaseReasonCategory(input.category)) {
    throw new Error("Choose a reason category before releasing.");
  }

  const reasoning = String(input.reasoning ?? "").trim();
  if (reasoning.length < MIN_RELEASE_REASONING_CHARS) {
    throw new Error(
      `Write at least ${MIN_RELEASE_REASONING_CHARS} characters of reasoning — this override is only worth having because it is recorded.`
    );
  }

  const recordedBy = (input.recordedBy || "").trim() || "CIO";
  // P2-REASONLENGTH: budgeted against the release LINE's own capacity, not against what is
  // left over after the lock's existing notes. The old budget went to zero on a lock
  // recorded from the Trade Desk with a thorough "why" and this function then refused the
  // release outright — the CIO lost the control because he had explained himself properly.
  // The server clips the older note to make room and reports what it clipped.
  const budget = releaseReasonAllowance({ existingNotes: input.existingNotes, recordedBy }).maxChars;
  const reason = composeReleaseReason(input.category, reasoning, budget);
  if (reason.trim().length === 0) {
    // Only reachable with a recordedBy long enough to consume the whole column, which the
    // handler's own 64-character actor field cannot produce. Kept so the function still has
    // no path that sends an empty reason (which the handler would swap for its default).
    throw new Error(
      "The signed-in user name is too long to leave room for any reason on this lock. Release it from the desk instead."
    );
  }

  return {
    action: HARVEST_RELEASE_ACTION,
    lockId,
    reason,
    recordedBy,
  };
}

/* --------------------------------------------------------------------------------------
 * Reading the answer. Never assert a release from HTTP 200 alone.
 * ----------------------------------------------------------------------------------- */

export type ReleaseOutcome = {
  /** True ONLY when the server said so in its body. */
  released: boolean;
  /** What to put on screen, in full sentences. */
  message: string;
};

/**
 * home_run_redeployment.py:692 is the only success shape:
 *     {"status": "success", "lockId": <int>, "released": True}
 *
 * Everything else that arrives with a 200 is unexplained, and "the request did not error"
 * is not the same claim as "the capital was released". The `WHERE ... AND Status='ACTIVE'`
 * clause means a release can legitimately affect zero rows (already released, already
 * expired, wrong id) — the server turns that into a 400, but a UI that reads 200 as done
 * would tell the CIO capital is free when it is not. So the body is checked, and anything
 * unrecognised is reported as UNCONFIRMED, not as failed and not as done.
 */
export function interpretReleaseResponse(
  httpStatus: number,
  payload: unknown,
  requestedLockId: number
): ReleaseOutcome {
  const body = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
  const serverError = body && typeof body.error === "string" && body.error.trim() ? body.error.trim() : null;

  if (httpStatus < 200 || httpStatus >= 300) {
    return {
      released: false,
      message: `${serverError || `The release failed (HTTP ${httpStatus}).`} The lockout is unchanged and the capital is still withheld.`,
    };
  }

  const released = body?.released === true;
  const success = body?.status === "success";
  const echoedId = typeof body?.lockId === "number" ? body.lockId : null;

  if (released && success && (echoedId === null || echoedId === requestedLockId)) {
    return {
      released: true,
      message:
        "Released. The capital is back in the buy budget on the next read, and the release is on the audit trail with your reasoning.",
    };
  }

  if (echoedId !== null && echoedId !== requestedLockId) {
    return {
      released: false,
      message: `The server answered about lock ${echoedId}, not lock ${requestedLockId}. Treat this release as UNCONFIRMED — reload the harvest panel and check the lockout before deploying anything.`,
    };
  }

  return {
    released: false,
    message:
      `${serverError || "The server returned success but did not confirm a release."} Treat this release as UNCONFIRMED — reload the harvest panel and check whether the lockout is still active before deploying anything.`,
  };
}

/* --------------------------------------------------------------------------------------
 * The brake: what the payload can and cannot tell us about past releases.
 * ----------------------------------------------------------------------------------- */

export type ReleaseHistoryEntry = {
  lockId: number | null;
  displayName: string;
  /** The whole lock — releases are all-or-nothing in v1. */
  amount: number | null;
  category: ReleaseReasonCategory | null;
  reasoning: string;
  releasedBy: string | null;
  /** The date the clock WOULD have run to. Available. */
  unlockAtUTC: string | null;
};

/**
 * WHAT IS MISSING AND WHY IT IS NOT INVENTED.
 *
 * The release timestamp exists in the database — release_home_run_lock stamps
 * `UpdatedAtUTC = SYSUTCDATETIME()` (home_run_redeployment.py:672) — but it never reaches
 * the browser: `_serialize_lock()` (home_run_redeployment.py:391-414) publishes neither
 * `UpdatedAtUTC` nor `CreatedAtUTC`, and `_recent_lock_rows()` (:262-276) does not even
 * SELECT them. So the date of a past release, and therefore how many days early it was,
 * cannot be derived from any payload this dashboard receives. v1 says so on screen rather
 * than guessing from `unlockAtUTC` (which would be wrong by up to the whole lock period)
 * or reading a field the backend does not send.
 */
export const RELEASE_HISTORY_GAPS = {
  releaseDateAvailable: false,
  daysEarlyAvailable: false,
  note:
    "Release dates are not shown: the harvest payload does not publish the release timestamp " +
    "(_serialize_lock omits UpdatedAtUTC), so days-early cannot be derived without inventing it. " +
    "Amounts and reasoning below are read from the payload; the full record is in dbo.TradeAudit " +
    "under HOME_RUN_LOCK_RELEASED.",
} as const;

const numOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * Pull past releases out of `recentLocks` (build_home_run_dashboard_payload -> TOP 25
 * ordered by CreatedAtUTC DESC).
 *
 * The reasoning is recovered from the LAST "Released by {who}: {reason}" line the server
 * appended to Notes, because Notes can already contain the harvest note and, in principle,
 * more than one appended line. A lock released before this control existed (or by the
 * handler's own "manual CIO release" default) parses to a null category, which is honest:
 * it was not categorised.
 */
export function parseReleaseHistory(recentLocks: unknown): ReleaseHistoryEntry[] {
  if (!Array.isArray(recentLocks)) return [];
  const out: ReleaseHistoryEntry[] = [];

  for (const raw of recentLocks) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    if (String(row.status ?? "").trim().toUpperCase() !== "RELEASED") continue;

    const notes = typeof row.notes === "string" ? row.notes : "";
    let releasedBy: string | null = null;
    let reasonText = "";
    for (const line of notes.split(/\r?\n/)) {
      const m = new RegExp(`^${RELEASE_NOTE_PREFIX}(.*?)${RELEASE_NOTE_JOINER}([\\s\\S]*)$`).exec(line.trim());
      if (m) {
        releasedBy = m[1].trim() || null;
        reasonText = m[2].trim();
      }
    }

    const parsed = parseComposedReason(reasonText);
    out.push({
      lockId: numOrNull(row.lockId),
      displayName:
        (typeof row.displayName === "string" && row.displayName.trim()) ||
        (row.contractId != null ? String(row.contractId) : "—"),
      amount: numOrNull(row.harvestedNotional),
      category: parsed.category,
      reasoning: parsed.reasoning,
      releasedBy,
      unlockAtUTC: typeof row.unlockAtUTC === "string" ? row.unlockAtUTC : null,
    });
  }

  return out;
}

/* --------------------------------------------------------------------------------------
 * Research context shown beside the decision.
 * ----------------------------------------------------------------------------------- */

/**
 * DRAFT research context — HYPOTHETICAL / BACKTESTED, not investor-facing, not a
 * recommendation, and not a reason to release or to wait. Four lines, because context
 * nobody reads is not context.
 *
 * Transcribed from the project's own studies, stated as what was and was NOT established:
 *
 *   HelionBacktest/harvest_event_study_outputs/delay_sweep/STEP1B_DELAY_SWEEP_SUMMARY.md
 *     - Headline verdict: INCONCLUSIVE. In the SUPERIORITY table every delay (0/15/30/45/
 *       90/120) fails every gate against the incumbent 60; in the HARM table every delay
 *       fails too. State column: 0, 15, 90 "NOT DISTINGUISHABLE FROM 60"; 30, 45, 120
 *       "UNRESOLVED". Tier-1 and Tier-2 flatness both NOT ATTAINED.
 *     - d = 0 (redeploy immediately), BASELINE / H126td / net: IS paired median -0.283%
 *       of NAV per event, 90% CI [-0.552%, -0.119%] — excludes zero. OOS on the same cell
 *       is +0.056%, CI [+0.023%, +0.176%] — the opposite sign. So the adverse reading of
 *       immediate redeployment is IN-SAMPLE ONLY and reverses out of sample. Saying it
 *       "has adverse evidence" without that reversal would overstate it.
 *     - Every harvest in the sample fired in an uptrend (140/140 above the 200-day),
 *       by construction: a long call must double to trigger one. It says nothing about
 *       bear markets.
 *
 *   Convexity-cheapness study P1, closed 2026-06-09 — no entry-quality edge found.
 */
export const RELEASE_RESEARCH_CONTEXT: ReadonlyArray<string> = [
  "The 60 days is a discipline default, not a calibrated optimum: the delay sweep's verdict was INCONCLUSIVE.",
  "30, 45 and 60 could not be told apart — no delay tested beat 60 on any pre-registered gate, and none was shown materially worse.",
  "Redeploying immediately is the only variant with any adverse reading (in-sample −0.28% of NAV per event vs waiting), but it reverses out-of-sample. Treat it as a caution, not a finding.",
  "“Options look cheap” is not evidence for releasing: entry-cheapness gates have failed testing (convexity-cheapness study, closed 2026-06-09).",
];

/** Label the block must carry. The studies are backtested and uptrend-only. */
export const RELEASE_RESEARCH_LABEL =
  "Hypothetical / backtested research context — every harvest studied fired in an uptrend, so none of it speaks to a bear market. Not a recommendation.";
